import { useCallback } from 'react';
import { flushSync } from 'react-dom';

/**
 * Custom hook for managing all game event handlers
 * Consolidates socket event handling logic for GameRoom
 */
const useGameEventHandlers = ({
  mountedRef,
  setPlayers,
  setJudge,
  setCurrentBlackCard,
  setRound,
  setScores,
  setMaxRounds,
  setWriteInsEnabled,
  setGameState,
  username,
  gameState,
  setIsRoomCreator,
  setRoomCreatorPlayerId,
  setIsDeputy,
  setDeputyPlayerId,
  setCoHostEnabled,
  trackRoundStarted,
  trackConnectionStatus,
  setPlayerHand,
  setSubmissionStatus,
  setPresentationState,
  setSubmissions,
  setSelectedCards,
  setHasSubmitted,
  setWriteInCards,
  trackRoundCompleted,
  roomCode,
  round,
  setPreviousBlackCard,
  currentBlackCard,
  setNextJudge,
  setRoundWinner,
  setRoundWinners,
  setIsDoubleAwardRound,
  setWinningSubmission,
  setPointsAwarded,
  setPointsBreakdown,
  setResultsCountdown,
  cleanupFunctionsRef,
  setIsWagering,
  setWageringPlayers,
  setChipsWagered,
  socketClient,
  clearRoomData,
  navigate,
  roundSkippedShownRef,
  currentPlayer,
  players,
  scores,
  trackGameCompleted,
  totalRoundsPlayed,
  roomCreatorPlayerId,
  setGameFinished,
  setTotalRoundsPlayed,
  trackError,
  addToast,
  refetchWriteInStats,
  setError,
  setConnectionStatus,
  setTimerActive,
  setTimerType,
  setTimerDuration,
  setTimerRemaining,
  setTimerMessage,
  setTimerTargetPlayer,
  timerIntervalRef,
  setHasSkippedThisRound,
  setCurrentPlayer,
  setDesignatedFirstCzarId,
  setAllowSpectatorPromotion,
  setRhpDiscardEnabled,
  setRhpDoubleAwardEnabled,
  setDoubleAwardUsedMap,
  setRhpEnhancedWageringEnabled,
}) => {
  const handleGameStateUpdate = useCallback(
    (newState) => {
      if (!mountedRef.current) return;
      console.log('📊 Game state updated:', newState);
      console.log('🔍 DEBUG - Judge from backend:', newState.judge);
      console.log('🔍 DEBUG - JudgeId from backend:', newState.judgeId);
      setPlayers(newState.players || []);
      setJudge(newState.judge);
      setCurrentBlackCard(newState.currentBlackCard);
      // Backend is 0-indexed, frontend displays 1-indexed
      setRound((newState.currentRound ?? 0) + 1);
      setScores(newState.scores || {});

      // Refresh the current user's own cached player object (id, socketId,
      // isSpectator, ...) on every full-state hydration - not just at mount.
      // Without this, currentPlayer.socketId goes stale across any reconnect
      // that gets a new socket.id (the default Socket.IO behavior, since this
      // app doesn't enable connectionStateRecovery), silently breaking the
      // scores[currentPlayer?.socketId] fallback used for the player's own
      // score display. Same identity match as the mount-time fetch in
      // GameRoom.js's initializeGameFromBackend.
      const updatedCurrentPlayer = newState.players?.find((p) => p.username === username);
      setCurrentPlayer(updatedCurrentPlayer);

      // Extract designatedFirstCzarId from game state (for page refresh/rejoin)
      if (newState.designatedFirstCzarId !== undefined) {
        setDesignatedFirstCzarId(newState.designatedFirstCzarId);
      }
      // Extract spectator promotion setting from game state
      if (newState.allowSpectatorPromotion !== undefined) {
        setAllowSpectatorPromotion(newState.allowSpectatorPromotion);
      }
      // Extract RHP discard enabled setting from game state
      if (newState.rhpDiscardEnabled !== undefined) {
        setRhpDiscardEnabled(newState.rhpDiscardEnabled);
      }
      // Extract RHP Judge Double Award setting and usage map from game state
      if (newState.rhpDoubleAwardEnabled !== undefined) {
        setRhpDoubleAwardEnabled(newState.rhpDoubleAwardEnabled);
      }
      if (newState.playerDoubleAwardUsed !== undefined) {
        setDoubleAwardUsedMap(newState.playerDoubleAwardUsed);
      }
      // Extract RHP Enhanced Wagering setting from game state
      if (newState.rhpEnhancedWageringEnabled !== undefined) {
        setRhpEnhancedWageringEnabled(newState.rhpEnhancedWageringEnabled);
      }

      // Check if current user is room creator - compare player ID not socket ID
      if (newState.roomCreatorPlayerId) {
        // Find current player in the players list to get their ID
        const currentPlayerInList = (newState.players || []).find((p) => p.username === username);
        console.log('🔑🔍 DETAILED Room creator check:', {
          username: username,
          roomCreatorPlayerId: newState.roomCreatorPlayerId,
          currentPlayerInList: currentPlayerInList
            ? {
                id: currentPlayerInList.id,
                username: currentPlayerInList.username,
                socketId: currentPlayerInList.socketId,
                playerId: currentPlayerInList.playerId,
              }
            : 'NOT FOUND',
          allPlayers: (newState.players || []).map((p) => ({
            username: p.username,
            id: p.id,
            socketId: p.socketId,
            playerId: p.playerId,
          })),
        });

        if (currentPlayerInList) {
          // Check both id and playerId fields since backend might use either
          const isCreator =
            currentPlayerInList.id === newState.roomCreatorPlayerId ||
            currentPlayerInList.playerId === newState.roomCreatorPlayerId;
          setIsRoomCreator(isCreator);
          setRoomCreatorPlayerId(newState.roomCreatorPlayerId); // Set the room creator player ID

          const isPlayerDeputy =
            currentPlayerInList.id === newState.deputyPlayerId ||
            currentPlayerInList.playerId === newState.deputyPlayerId;
          setIsDeputy(isPlayerDeputy);
          setDeputyPlayerId(newState.deputyPlayerId);
          setCoHostEnabled(newState.coHostEnabled || false);

          console.log('🔑✅ Room creator/deputy result:', {
            currentPlayerId: currentPlayerInList.id,
            currentPlayerPlayerId: currentPlayerInList.playerId,
            roomCreatorPlayerId: newState.roomCreatorPlayerId,
            deputyPlayerId: newState.deputyPlayerId,
            isRoomCreator: isCreator,
            isDeputy: isPlayerDeputy,
          });
        } else {
          console.log('🔑❌ Current player not found in players list!');
        }
      } else {
        console.log('🔑⚠️ No roomCreatorPlayerId in game state!');
      }

      // FIXED: Update maxRounds when received from backend
      if (newState.maxRounds) {
        setMaxRounds(newState.maxRounds);
        console.log('🎯 Max rounds updated from game state:', newState.maxRounds);
      }

      // WRITE-IN CARDS: Update writeInsEnabled setting from game state
      if (typeof newState.writeInsEnabled === 'boolean') {
        setWriteInsEnabled(newState.writeInsEnabled);
        console.log('✍️ Write-ins enabled:', newState.writeInsEnabled);
      }

      // Update gameState from backend gamePhase
      if (newState.gamePhase && newState.gamePhase !== gameState) {
        setGameState(newState.gamePhase);
        console.log('🎯 gameState updated from backend to:', newState.gamePhase);
      }

      // Track round start when game phase changes to playing
      if (newState.gamePhase === 'playing' && gameState !== 'playing') {
        trackRoundStarted();
      }
    },
    [
      mountedRef,
      setPlayers,
      setJudge,
      setCurrentBlackCard,
      setRound,
      setScores,
      username,
      setIsRoomCreator,
      setRoomCreatorPlayerId,
      setIsDeputy,
      setDeputyPlayerId,
      setCoHostEnabled,
      setMaxRounds,
      setWriteInsEnabled,
      setGameState,
      gameState,
      trackRoundStarted,
      setCurrentPlayer,
      setDesignatedFirstCzarId,
      setAllowSpectatorPromotion,
      setRhpDiscardEnabled,
      setRhpDoubleAwardEnabled,
      setDoubleAwardUsedMap,
      setRhpEnhancedWageringEnabled,
    ]
  );

  const handlePlayerHand = useCallback(
    (hand) => {
      if (!mountedRef.current) return;
      console.log('🃏 Received hand from backend:', hand);
      console.log('🃏 Hand length:', hand?.length);
      console.log('🃏 Sample card structure:', hand?.[0]);

      // CRITICAL: Verify all cards have proper IDs
      if (hand && Array.isArray(hand)) {
        const cardsWithoutIds = hand.filter((card) => !card.id);
        if (cardsWithoutIds.length > 0) {
          console.error('❌ Cards received without IDs:', cardsWithoutIds);
        }

        // FIXED: Store hand exactly as received from backend
        setPlayerHand(
          hand.map((card) => ({
            id: card.id,
            text: card.text,
          }))
        );

        console.log('✅ Player hand updated with preserved IDs');
      } else {
        console.error('❌ Invalid hand received:', hand);
        setPlayerHand([]);
      }
    },
    [mountedRef, setPlayerHand]
  );

  const handleSubmissionUpdate = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.log('📊 Submission update:', data);
      setSubmissionStatus(data);
      if (data.submitterName) {
        console.log(`✅ ${data.submitterName} submitted cards`);
      }
    },
    [mountedRef, setSubmissionStatus]
  );

  const handlePresentationPhaseStarted = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.log('🎭 Presentation phase started:', data);

      setGameState('presentation');

      setPresentationState({
        isActive: true,
        currentIndex: -1, // Not started yet
        totalSubmissions: data.totalSubmissions,
        currentSubmission: null,
        showAllSubmissions: false,
      });

      // Clear previous submissions to start fresh
      setSubmissions([]);
    },
    [mountedRef, setGameState, setPresentationState, setSubmissions]
  );

  const handlePresentationUpdated = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.log('🎭 Presentation updated:', data);

      setPresentationState({
        isActive: true,
        currentIndex: data.currentIndex,
        totalSubmissions: data.totalSubmissions,
        currentSubmission: data.currentSubmission,
        showAllSubmissions: data.showAllSubmissions,
      });
    },
    [mountedRef, setPresentationState]
  );

  const handleVotingPhaseStarted = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.log('🗳️ Voting phase started:', data);

      // Log the presentation order received from backend
      if (data.submissions && Array.isArray(data.submissions)) {
        console.log(
          '📥 PRESENTATION ORDER RECEIVED:',
          data.submissions
            .map((s, i) => `${i + 1}. ${s.playerName}: ${s.cards?.[0]?.text?.substring(0, 30)}...`)
            .join(' | ')
        );
      }

      // Clear presentation state when entering voting
      setPresentationState({
        isActive: false,
        currentIndex: -1,
        totalSubmissions: 0,
        currentSubmission: null,
        showAllSubmissions: false,
      });

      // Detailed logging of submissions data
      console.log('📊 Submissions data received:');
      console.log(
        '  - Data type:',
        Array.isArray(data.submissions) ? 'Array' : typeof data.submissions
      );
      console.log('  - Total submissions:', data.submissions?.length || 0);

      // Log each submission in detail
      (data.submissions || []).forEach((submission, index) => {
        console.log(`  - Submission ${index}:`, {
          playerName: submission.playerName,
          playerId: submission.playerId,
          cardCount: submission.cards?.length || 0,
          cards: submission.cards?.map((c) => ({
            id: c.id,
            text: c.text?.substring(0, 50) + '...',
          })),
        });
      });

      // Check if any bot submissions are present
      const botSubmissions = (data.submissions || []).filter((submission) =>
        submission.playerName?.includes('Bot')
      );
      console.log(`🤖 Bot submissions found: ${botSubmissions.length}`);
      botSubmissions.forEach((submission, index) => {
        console.log(
          `  🤖 ${submission?.playerName || 'Unknown Bot'}: ${submission?.cards?.length || 0} cards`
        );
      });

      setGameState('voting');
      console.log('🎯 gameState changed to: voting');
      console.log(
        '🎯 State immediately after setGameState - gameState should be voting on next render'
      );

      // Store the time when voting phase started to prevent immediate overrides
      window.votingPhaseStartTime = Date.now();

      // Log when setting submissions to state
      console.log(
        '📦 PRESENTATION CARDS SET TO STATE:',
        data.submissions.map((s, i) => `${i + 1}. ${s.playerName}`).join(' | ')
      );
      setSubmissions(data.submissions);
      setSelectedCards([]);
      setHasSubmitted(false);

      // WRITE-IN CARDS: Clear write-ins for new round
      setWriteInCards([]);

      // CRITICAL FIX: Update submissionStatus to reflect all submissions are in
      setSubmissionStatus({
        submitted: data.submissions.length,
        total: data.submissions.length,
      });
      console.log('📊 Updated submissionStatus:', {
        submitted: data.submissions.length,
        total: data.submissions.length,
      });

      console.log('✅ Frontend state updated - submissions set to:', data.submissions);
    },
    [
      mountedRef,
      setPresentationState,
      setGameState,
      setSubmissions,
      setSelectedCards,
      setHasSubmitted,
      setWriteInCards,
      setSubmissionStatus,
    ]
  );

  const handleRoundEnded = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.log('🏆 Round ended:', data);
      console.log('🏆 Current gameState when round ended:', gameState);
      console.log(
        '🏆 Time since component mounted:',
        Date.now() - (window.gameRoomMountTime || Date.now())
      );

      // TIMING FIX: If we just started voting, delay the round end to allow voting UI to render
      const timeSinceVotingStarted = Date.now() - (window.votingPhaseStartTime || 0);
      const minimumVotingTime = 1000; // 1 second minimum to see voting UI

      if (gameState === 'voting' && timeSinceVotingStarted < minimumVotingTime) {
        console.log(
          `⏰ Delaying round end for ${minimumVotingTime - timeSinceVotingStarted}ms to show voting UI`
        );
        setTimeout(() => {
          if (mountedRef.current) {
            handleRoundEnded(data);
          }
        }, minimumVotingTime - timeSinceVotingStarted);
        return;
      }

      // Track round completion
      trackRoundCompleted(roomCode, data.round || round);

      // NEW: Store the current black card as the previous one
      setPreviousBlackCard(currentBlackCard);

      // NEW: Store the next Judge for results display
      if (data.nextJudge) {
        setNextJudge(data.nextJudge);
        console.log('⚖️ Next Judge for results:', data.nextJudge.username);
      } else {
        setNextJudge(null); // Final round, no next Judge
        console.log('⚖️ Final round - no next Judge');
      }

      setGameState('results');
      console.log('🎯 gameState changed to: results');
      setRoundWinner(data.winner);
      // ISSUE #271: RHP Judge Double Award sends both winners via `winners`/`isDoubleAward`
      setRoundWinners(data.winners || []);
      setIsDoubleAwardRound(!!data.isDoubleAward);
      setWinningSubmission(data.winningSubmission); // NEW: Store the winning submission
      setPointsAwarded(data.pointsAwarded); // #190: Store points awarded
      setPointsBreakdown(data.pointsBreakdown); // #190: Store points breakdown

      if (data.scoreboard) {
        const scoresObj = data.scoreboard.reduce((acc, player) => {
          if (player?.id) {
            acc[player.id] = player?.score || 0;
          }
          return acc;
        }, {});
        setScores(scoresObj);
      }

      // Update round number if provided
      if (data.newRound) {
        setRound(data.newRound);
      }

      setSubmissions([]);
      setSubmissionStatus({ submitted: 0, total: 0 });

      // Start countdown at 5 seconds (changed from 3 to match backend delay)
      setResultsCountdown(5);

      // Create countdown interval
      let countdown = 5;
      const countdownInterval = setInterval(() => {
        countdown--;
        if (mountedRef.current) {
          setResultsCountdown(countdown);
        }
        if (countdown <= 0) {
          clearInterval(countdownInterval);
        }
      }, 1000);

      // Store cleanup function for the interval
      cleanupFunctionsRef.current.push(() => clearInterval(countdownInterval));

      // MEMORY LEAK FIX: Clear timeout on unmount
      // NOTE: Judge and black card updates now handled by 'round-started' event
      const timeout = setTimeout(() => {
        if (!mountedRef.current) return;

        // Don't transition to czar-picking here - let the round-started event handle it
        // This prevents race conditions between round-ended and round-started events
        setRoundWinner(null);
        setRoundWinners([]);
        setIsDoubleAwardRound(false);
        setWinningSubmission(null);
        setPointsAwarded(null);
        setPointsBreakdown(null);
        setResultsCountdown(null);
        setSelectedCards([]);
        setHasSubmitted(false);

        // WRITE-IN CARDS: Clear write-ins for new round
        setWriteInCards([]);

        // CARD CZAR CONTROLS: Reset skip tracking for new round
        setHasSkippedThisRound(false);
      }, 5000);

      cleanupFunctionsRef.current.push(() => clearTimeout(timeout));
    },
    [
      mountedRef,
      gameState,
      trackRoundCompleted,
      roomCode,
      round,
      setPreviousBlackCard,
      currentBlackCard,
      setNextJudge,
      setGameState,
      setRoundWinner,
      setRoundWinners,
      setIsDoubleAwardRound,
      setWinningSubmission,
      setPointsAwarded,
      setPointsBreakdown,
      setScores,
      setRound,
      setSubmissions,
      setSubmissionStatus,
      setResultsCountdown,
      cleanupFunctionsRef,
      setSelectedCards,
      setHasSubmitted,
      setWriteInCards,
      setHasSkippedThisRound,
    ]
  );

  const handleWagerConfirmed = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.log('💰 Wager confirmed:', data);
      setIsWagering(true);
      // RHP: Enhanced Wagering — sync to the server-confirmed chip count (may be clamped)
      if (data?.chipsWagered !== undefined) {
        setChipsWagered(data.chipsWagered);
      }
    },
    [mountedRef, setIsWagering, setChipsWagered]
  );

  const handleWagerCancelled = useCallback(() => {
    if (!mountedRef.current) return;
    console.log('💰 Wager cancelled');
    setIsWagering(false);
    setChipsWagered(1);
  }, [mountedRef, setIsWagering, setChipsWagered]);

  const handleWagerUpdate = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.log('💰 Wager update:', data);

      // Update wagering players list
      setWageringPlayers((prevPlayers) => {
        const filtered = prevPlayers.filter((p) => p.playerId !== data.playerId);
        if (data.isWagering) {
          return [...filtered, data];
        }
        return filtered;
      });
    },
    [mountedRef, setWageringPlayers]
  );

  const handlePlayerKicked = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.log('👢 Player kicked:', data);

      // Immediately set unmounted to prevent duplicate events (Safari bug fix)
      mountedRef.current = false;

      // Clear localStorage to prevent stale state on rejoin
      localStorage.removeItem('gameState');
      console.log('🧹 Cleared gameState from localStorage to prevent stale rejoin state');

      // Show notification and redirect to landing page
      alert(data.message || 'You have been removed from the game');

      // Disconnect socket and navigate
      socketClient.disconnect();
      clearRoomData();
      navigate('/');
    },
    [mountedRef, socketClient, clearRoomData, navigate]
  );

  const handlePlayerDemoted = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.log('⬇️ Player demoted to spectator:', data);

      // Keep the stored role in sync so a later reload's rejoin-fallback
      // (GameRoom's initializeGameFromBackend) requests the right role
      // instead of the one this player had when they first joined.
      localStorage.setItem('isSpectator', 'true');

      // Backend sends this event only to the demoted player
      alert(
        data.message ||
          'You have been moved to voyeur mode. You can watch the game but cannot participate.'
      );

      // The game state will be updated via the normal 'room-state' or 'game-state-update' events
      // No need to navigate away or disconnect - player stays in the room
    },
    [mountedRef]
  );

  const handleRoundStarted = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.log('🔄 Round started:', data);

      // Update round number (backend is 0-indexed, frontend displays 1-indexed)
      if (data.newRound !== undefined) {
        setRound((data.newRound ?? 0) + 1);
      }

      // Update Judge
      if (data.newJudge) {
        setJudge(data.newJudge);
        console.log('⚖️ Judge updated to:', data.newJudge.username);
      }

      // Update black card
      if (data.newBlackCard) {
        setCurrentBlackCard(data.newBlackCard);
        console.log('🖤 Black card updated to:', data.newBlackCard.text);
      }

      // Ensure game state is czar-picking
      setGameState('czar-picking');
      console.log('🎯 gameState changed to: czar-picking (round-started)');
    },
    [mountedRef, setRound, setJudge, setCurrentBlackCard, setGameState]
  );

  const handleRoundSkipped = useCallback(
    (data) => {
      if (!mountedRef.current) return;

      // Prevent duplicate alerts using debounce flag
      if (roundSkippedShownRef.current) {
        console.log('🚫 Round skipped alert already shown, ignoring duplicate event');
        return;
      }
      roundSkippedShownRef.current = true;

      console.log('🚫 Round skipped:', data);

      // Update to new round and czar FIRST
      // Backend is 0-indexed, frontend displays 1-indexed
      if (data.newRound !== undefined) {
        setRound((data.newRound ?? 0) + 1);
      }

      // Update game state to czar-picking phase
      setGameState('czar-picking');
      console.log('🎯 gameState changed to: czar-picking (round skipped)');

      // Clear any existing submissions and voting state
      setSubmissions([]);
      setSubmissionStatus({ submitted: 0, total: 0 });
      setPresentationState({
        isActive: false,
        currentIndex: -1,
        totalSubmissions: 0,
        currentSubmission: null,
        showAllSubmissions: false,
      });
      setSelectedCards([]);
      setHasSubmitted(false);
      setWriteInCards([]);
      setHasSkippedThisRound(false);

      // Show toast notification AFTER state updates
      setTimeout(() => {
        if (mountedRef.current) {
          // Show toast for round skip
          const skipMessage = data.message || 'Round has been skipped';
          addToast(skipMessage, 'warning', 4000);

          // Show additional toast for new Judge if available
          if (data.newJudge) {
            setTimeout(() => {
              addToast(`Judge rotated to ${data.newJudge}`, 'info', 4000);
            }, 500);
          }

          // Reset the flag after a short delay to allow future round-skipped events
          setTimeout(() => {
            roundSkippedShownRef.current = false;
          }, 1000);
        }
      }, 100);
    },
    [
      mountedRef,
      roundSkippedShownRef,
      setRound,
      setGameState,
      setSubmissions,
      setSubmissionStatus,
      setPresentationState,
      setSelectedCards,
      setHasSubmitted,
      setWriteInCards,
      setHasSkippedThisRound,
      addToast,
    ]
  );

  const handleDeckReset = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.log('🔄 Deck reset notification received:', data);

      // Show toast notification to players
      addToast(
        data.message || '🔄 All questions used! Deck shuffled - questions will now repeat',
        'warning',
        5000
      );
    },
    [mountedRef, addToast]
  );

  const handleScoringDiscrepancy = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.error('❌❌❌ REAL-TIME SCORING DISCREPANCY DETECTED ❌❌❌');
      console.error('Round:', data.roundNumber);
      console.error('Rounds completed:', data.roundsCompleted);
      console.error('Total points:', data.totalPoints);
      console.error('Missing points:', data.missingPoints);

      // Only show alert to room owner
      if (
        currentPlayer?.id === data.roomOwner ||
        currentPlayer?.socketId === data.roomOwner ||
        currentPlayer?.playerId === data.roomOwner
      ) {
        console.log('🔑 Current user IS room owner - showing alert');
        alert(
          `⚠️ SCORING ISSUE DETECTED (Round ${data.roundNumber}) ⚠️\n\n` +
            `As the room owner, you're being notified:\n\n` +
            `Round: ${data.roundNumber}\n` +
            `Rounds Completed: ${data.roundsCompleted}\n` +
            `Total Points: ${data.totalPoints}\n` +
            `Expected Minimum: ${data.expectedPoints}\n` +
            `Missing: ${data.missingPoints} points\n` +
            `Failure Rate: ${data.failureRate}%\n\n` +
            `An automatic bug report has been sent to the developer.\n` +
            `Room Code: ${roomCode}\n\n` +
            `The game will continue, but scoring data may be incomplete.`
        );
      } else {
        console.log('🔒 Current user is NOT room owner - alert suppressed');
      }
    },
    [mountedRef, currentPlayer, roomCode]
  );

  const handleGameEnded = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.log('🎉 Game ended:', data);

      // SCORING VALIDATION: Check for discrepancies reported by backend (end-of-game validation)
      if (data.scoringDiscrepancy) {
        console.error('❌❌❌ SCORING DISCREPANCY DETECTED (End of Game) ❌❌❌');
        console.error('Backend reports:', data.scoringDiscrepancy);
        console.error(
          `Missing ${data.scoringDiscrepancy.missingPoints} points (${data.scoringDiscrepancy.failureRate}% failure rate)`
        );

        // Only show alert to room owner
        if (
          currentPlayer?.id === roomCreatorPlayerId ||
          currentPlayer?.socketId === roomCreatorPlayerId ||
          currentPlayer?.playerId === roomCreatorPlayerId
        ) {
          console.log('🔑 Current user IS room owner - showing end-of-game alert');
          alert(
            `⚠️ SCORING ISSUE DETECTED (Final Summary) ⚠️\n\n` +
              `As the room owner, you're being notified:\n\n` +
              `Played: ${data.scoringDiscrepancy.totalRounds} rounds\n` +
              `Awarded: ${data.scoringDiscrepancy.totalPoints} points\n` +
              `Missing: ${data.scoringDiscrepancy.missingPoints} points\n` +
              `Failure Rate: ${data.scoringDiscrepancy.failureRate}%\n\n` +
              `Some rounds did not award points correctly.\n` +
              `An automatic bug report has been sent to the developer.\n\n` +
              `Room Code: ${roomCode}`
          );
        } else {
          console.log('🔒 Current user is NOT room owner - alert suppressed');
        }
      }

      // Track game completion
      const winner =
        data.winner ||
        players.find(
          (p) => (scores[p?.id] || scores[p?.socketId] || 0) === Math.max(...Object.values(scores))
        );

      trackGameCompleted(
        roomCode,
        winner?.username || 'Unknown',
        players.length,
        data.totalRounds || totalRoundsPlayed || round
      );

      setGameState('finished');
      console.log('🎯 gameState changed to: finished');
      setGameFinished(true);

      // Update final scores
      if (data.finalScores) {
        setScores(data.finalScores);
      }

      // REMOVED PLAYERS: Update players list with finalResults (includes removed players)
      if (data.finalResults) {
        setPlayers(data.finalResults);
        console.log('📊 Final results (including removed players):', data.finalResults);
      }

      // CRITICAL FIX: Store the total rounds played from backend
      if (data.totalRounds) {
        setTotalRoundsPlayed(data.totalRounds);
        console.log('📊 Total rounds played:', data.totalRounds);
      }
    },
    [
      mountedRef,
      currentPlayer,
      roomCreatorPlayerId,
      roomCode,
      players,
      scores,
      trackGameCompleted,
      totalRoundsPlayed,
      round,
      setGameState,
      setGameFinished,
      setScores,
      setTotalRoundsPlayed,
      setPlayers,
    ]
  );

  const handleSocketError = useCallback(
    (error) => {
      if (!mountedRef.current) return;
      console.error('❌ Server error:', error);
      trackError('socket_error', { error: error.toString(), roomCode });

      // Handle write-in rate limiting errors with toast notifications
      if (error?.type === 'write_in_rate_limit') {
        // Determine toast type based on error reason
        let toastType = 'warning';
        if (error.reason === 'PER_ROUND_LIMIT' || error.reason === 'PER_IP_LIMIT') {
          toastType = 'error';
        }

        // Show toast notification
        addToast(error.message || 'Rate limit exceeded', toastType, 5000);

        // Refetch stats to update UI
        refetchWriteInStats();

        // Don't set general error state for rate limits, just show toast
        return;
      }

      // For other errors, set error state
      setError(typeof error === 'string' ? error : error?.message || 'Server error');
    },
    [mountedRef, trackError, roomCode, addToast, refetchWriteInStats, setError]
  );

  const handleSubmissionError = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      const message = typeof data === 'string' ? data : data?.message;
      console.warn('❌ Submission rejected:', message);
      // BUGFIX #245: reset hasSubmitted so a rejected submission never leaves the
      // client stuck showing "submitted" while the server never counted it
      setHasSubmitted(false);
      setSelectedCards([]);
      addToast(message || 'Your submission was rejected. Please try again.', 'error', 5000);
    },
    [mountedRef, setHasSubmitted, setSelectedCards, addToast]
  );

  const handleSocketConnect = useCallback(() => {
    if (!mountedRef.current) return;
    console.log('✅ Socket connected in GameRoom');
    setConnectionStatus('connected');
    // A prior connect_error/reconnect_failed may have left `error` set - the
    // render gate below checks `connectionStatus === 'error' || error`, so
    // without this a real, successful reconnect would still be stuck showing
    // the Connection Error screen forever, with the game silently continuing
    // to update in the background behind it.
    setError(null);
    trackConnectionStatus('reconnected', roomCode);
  }, [mountedRef, setConnectionStatus, setError, trackConnectionStatus, roomCode]);

  const handleSocketConnectError = useCallback(
    (error) => {
      if (!mountedRef.current) return;
      console.error('❌ Socket connection error in GameRoom:', error);
      setConnectionStatus('error');
      trackConnectionStatus('connection_error', roomCode);
      setError(error.message);
    },
    [mountedRef, setConnectionStatus, trackConnectionStatus, roomCode, setError]
  );

  // Socket.io gives up retrying after its reconnectionAttempts cap and never
  // tries again on its own - this is the only signal that a manual retry
  // control needs to be shown instead of waiting on a background retry.
  const handleSocketReconnectFailed = useCallback(() => {
    if (!mountedRef.current) return;
    console.error('❌ Reconnection permanently failed in GameRoom');
    setConnectionStatus('reconnect_failed');
    trackConnectionStatus('reconnect_failed', roomCode);
  }, [mountedRef, setConnectionStatus, trackConnectionStatus, roomCode]);

  const handleBlackCardChanged = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.log('✍️ Black card changed by Judge:', data);

      setCurrentBlackCard(data.newBlackCard);
      setGameState('playing');
      setSelectedCards([]);
      setHasSubmitted(false);
      setSubmissions([]);
      setSubmissionStatus({ submitted: 0, total: 0 });

      // Clear write-in cards for fresh round
      setWriteInCards([]);
    },
    [
      mountedRef,
      setCurrentBlackCard,
      setGameState,
      setSelectedCards,
      setHasSubmitted,
      setSubmissions,
      setSubmissionStatus,
      setWriteInCards,
    ]
  );

  const handleBlackCardSkipped = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.log('⏭️ Black card skipped:', data);

      setCurrentBlackCard(data.newBlackCard);
      // Keep gameState as 'czar-picking' so Judge can still make choices
      // (gameState will be set by the backend's gamePhase in data.gameState)
      if (data.gameState?.gamePhase) {
        setGameState(data.gameState.gamePhase);
      }
      setSelectedCards([]);
      setHasSubmitted(false);
      setSubmissions([]);
      setSubmissionStatus({ submitted: 0, total: 0 });

      // Clear write-in cards for fresh round
      setWriteInCards([]);
    },
    [
      mountedRef,
      setCurrentBlackCard,
      setGameState,
      setSelectedCards,
      setHasSubmitted,
      setSubmissions,
      setSubmissionStatus,
      setWriteInCards,
    ]
  );

  const handleCzarConfirmedBlackCard = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.log('🚀 Judge confirmed black card:', data);

      // CRITICAL FIX: Use flushSync to force React to apply state clearing synchronously
      // This prevents React's automatic batching from combining these updates with gameState change
      flushSync(() => {
        setSelectedCards([]);
        setHasSubmitted(false);
        setSubmissions([]);
        setSubmissionStatus({ submitted: 0, total: 0 });
        setWriteInCards([]);
      });

      // THEN update game state and black card (after flush completes)
      setCurrentBlackCard(data.blackCard);
      setGameState('playing');

      // #85: Judge was tardy - card was auto-confirmed instead of manually picked
      if (data.auto) {
        addToast('⏱️ Judge was inactive - card auto-selected', 'warning', 4000);
      }
    },
    [
      mountedRef,
      setCurrentBlackCard,
      setGameState,
      setSelectedCards,
      setHasSubmitted,
      setSubmissions,
      setSubmissionStatus,
      setWriteInCards,
      addToast,
    ]
  );

  const handleTimerStarted = useCallback(
    (timerData) => {
      if (!mountedRef.current) return;
      console.log('⏰ Timer started:', timerData);

      // Safely handle null/undefined timerData
      if (!timerData) {
        console.warn('⚠️ Timer started with null/undefined data');
        return;
      }

      setTimerActive(true);
      setTimerType(timerData.type || 'escalation');
      setTimerDuration(timerData.duration || 60);
      setTimerRemaining(timerData.duration || 60);
      setTimerMessage(timerData.message || 'Timer active');
      setTimerTargetPlayer(timerData.targetPlayer || null);

      // Start countdown
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }

      const startTime = Date.now();
      const safeDuration = timerData.duration || 60;
      timerIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        const remaining = Math.max(0, safeDuration - elapsed);
        setTimerRemaining(remaining);

        if (remaining === 0) {
          clearInterval(timerIntervalRef.current);
          setTimerActive(false);
        }
      }, 1000);
    },
    [
      mountedRef,
      setTimerActive,
      setTimerType,
      setTimerDuration,
      setTimerRemaining,
      setTimerMessage,
      setTimerTargetPlayer,
      timerIntervalRef,
    ]
  );

  const handleTimerExpired = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.log('⏰ Timer expired:', data);

      // Clear timer state
      setTimerActive(false);
      setTimerType(null);
      setTimerRemaining(0);
      setTimerMessage('');
      setTimerTargetPlayer(null);

      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      // Show notification about auto-submitted players
      if (data.autoSubmittedPlayers && data.autoSubmittedPlayers.length > 0) {
        console.log(`⏰ Auto-submitted for: ${data.autoSubmittedPlayers.join(', ')}`);
      }
    },
    [
      mountedRef,
      setTimerActive,
      setTimerType,
      setTimerRemaining,
      setTimerMessage,
      setTimerTargetPlayer,
      timerIntervalRef,
    ]
  );

  const handleTimerCancelled = useCallback(
    (data) => {
      if (!mountedRef.current) return;
      console.log('⏰ Timer cancelled:', data);

      // Clear timer state (same as handleTimerExpired)
      setTimerActive(false);
      setTimerType(null);
      setTimerRemaining(0);
      setTimerMessage('');
      setTimerTargetPlayer(null);

      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      // Show success message
      console.log(`✅ ${data.message}`);
    },
    [
      mountedRef,
      setTimerActive,
      setTimerType,
      setTimerRemaining,
      setTimerMessage,
      setTimerTargetPlayer,
      timerIntervalRef,
    ]
  );

  return {
    handleGameStateUpdate,
    handlePlayerHand,
    handleSubmissionUpdate,
    handlePresentationPhaseStarted,
    handlePresentationUpdated,
    handleVotingPhaseStarted,
    handleRoundEnded,
    handleWagerConfirmed,
    handleWagerCancelled,
    handleWagerUpdate,
    handlePlayerKicked,
    handlePlayerDemoted,
    handleRoundStarted,
    handleRoundSkipped,
    handleDeckReset,
    handleScoringDiscrepancy,
    handleGameEnded,
    handleSocketError,
    handleSubmissionError,
    handleSocketConnect,
    handleSocketConnectError,
    handleSocketReconnectFailed,
    handleBlackCardChanged,
    handleBlackCardSkipped,
    handleCzarConfirmedBlackCard,
    handleTimerStarted,
    handleTimerExpired,
    handleTimerCancelled,
  };
};

export default useGameEventHandlers;
